import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { refundedStatusLabel, refundedVin } from '../../utils/refundOrderApi';

const modalShellClassName = 'flex max-h-[calc(100dvh-1.25rem)] w-full max-w-[32rem] flex-col overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)] min-[641px]:max-h-[calc(100dvh-3rem)]';
const modalHeaderClassName = 'shrink-0 px-4 pt-5 min-[641px]:px-6 min-[641px]:pt-6';
const modalBodyClassName = 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-5 pt-3 min-[641px]:px-6';
const modalFooterClassName = 'flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/70 px-4 py-3 min-[641px]:flex-row min-[641px]:justify-end min-[641px]:px-6 min-[641px]:py-4';
const modalPrimaryButtonClassName = 'inline-flex min-w-[92px] items-center justify-center rounded-[0.65rem] px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const modalSecondaryButtonClassName = 'inline-flex min-w-[84px] items-center justify-center rounded-[0.65rem] border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

const ChangeRow = ({ label, from, to }) => (
  <div className="grid gap-1 border-b border-rose-100 px-3 py-2.5 last:border-b-0 sm:grid-cols-[4.5rem_minmax(0,1fr)] sm:items-start sm:gap-3">
    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-rose-700">{label}</div>
    <div className="min-w-0 text-sm text-slate-800">
      <div className="break-all text-slate-600 line-through decoration-1 decoration-slate-800">{from || '-'}</div>
      <div className="mt-0.5 break-all font-semibold text-slate-900">{to || '-'}</div>
    </div>
  </div>
);

const RefundConfirmModal = ({
  open,
  vin,
  status,
  orderLabel,
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (open) setConfirmed(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const nextVin = refundedVin(vin);
  const nextStatus = refundedStatusLabel();

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden bg-slate-900/28 px-2.5 py-2.5 backdrop-blur-[3px] min-[641px]:px-4 min-[641px]:py-6">
      <div className={modalShellClassName} role="dialog" aria-modal="true" aria-labelledby="refund-order-title">
        <div className={modalHeaderClassName}>
          <div className="inline-flex items-center rounded-[0.55rem] bg-rose-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
            Refund
          </div>
          <div className="mt-2.5">
            <h3 id="refund-order-title" className="text-[0.95rem] font-semibold leading-6 text-slate-900">
              Mark this paid order as refunded?
            </h3>
          </div>
        </div>

        <div className={modalBodyClassName}>
          <p className="text-sm leading-6 text-slate-600">
            Refund the payment in Stripe manually. This only changes the order details here and does not send a refund to Stripe. Marking a refund changes the VIN and status only.
          </p>
          {orderLabel ? (
            <p className="mt-3 break-words text-sm font-medium text-slate-800">{orderLabel}</p>
          ) : null}

          <div className="mt-4 overflow-hidden rounded-2xl border border-rose-200 bg-rose-50/70">
            <div className="border-b border-rose-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-rose-800">
              Changes
            </div>
            <ChangeRow label="VIN" from={vin} to={nextVin} />
            <ChangeRow label="Status" from={status || 'Paid'} to={nextStatus} />
          </div>

          <div className="mt-4 flex items-start rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <div className="flex h-5 items-center">
              <input
                id="refund-order-confirmation"
                name="refund-order-confirmation"
                type="checkbox"
                checked={confirmed}
                disabled={loading}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-rose-700 focus:ring-rose-700"
              />
            </div>
            <div className="ml-3 min-w-0 text-sm">
              <label htmlFor="refund-order-confirmation" className="text-slate-700">
                I confirm this paid order should be marked as refunded
              </label>
            </div>
          </div>
        </div>

        <div className={modalFooterClassName}>
          <button
            type="button"
            className={`${modalPrimaryButtonClassName} bg-rose-700 hover:bg-rose-800 focus:ring-rose-400`}
            disabled={loading || !confirmed}
            onClick={() => {
              if (!confirmed || loading) return;
              onConfirm();
            }}
          >
            {loading ? 'Saving...' : 'Confirm'}
          </button>
          <button
            type="button"
            className={`${modalSecondaryButtonClassName} hover:border-slate-300 hover:bg-slate-100 focus:ring-slate-300`}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RefundConfirmModal;
